import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Box, CircularProgress, Typography, Button, Paper } from '@mui/material';
import LockClockIcon from '@mui/icons-material/LockClock';
import LockIcon from '@mui/icons-material/Lock';
import BlockIcon from '@mui/icons-material/Block';

// Layout
import Layout from './components/Layout/Layout';

// Context
import { UserProvider } from './context/UserContext';

// Pages
import Dashboard from './pages/Dashboard';
import ContractList from './pages/Contracts/ContractList';
import ContractDetail from './pages/Contracts/ContractDetail';
import CreateContract from './pages/Contracts/CreateContract';
import ObligationList from './pages/Obligations/ObligationList';
import ObligationDetail from './pages/Obligations/ObligationDetail';
import NotificationList from './pages/Notifications/NotificationList';
import ReportsPage from './pages/Reports/ReportsPage';
import AdminPage from './pages/Admin/AdminPage';
import PreferencesPage from './pages/Preferences';

// MSAL login
import { loginRequest } from './authConfig';
import { usersApi } from './services/api';

const theme = createTheme({
  palette: {
    primary: { main: '#1565c0', light: '#1976d2', dark: '#0d47a1' },
    secondary: { main: '#0288d1' },
    error: { main: '#c62828' },
    warning: { main: '#e65100' },
    success: { main: '#2e7d32' },
    background: { default: '#f4f6f9', paper: '#ffffff' }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 }
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: {
      styleOverrides: { root: { textTransform: 'none', fontWeight: 500 } }
    },
    MuiCard: {
      styleOverrides: { root: { boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0' } }
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 500 } }
    }
  }
});

type AuthState =
  | 'checking'
  | 'approved'
  | 'pending'        // 403 + error=pending_approval
  | 'deactivated'    // 403 + error=deactivated
  | 'invalid_token'  // 401 from /me — token rejected by backend
  | null;

function AuthGate({ children }: { children: React.ReactNode }) {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [approvalStatus, setApprovalStatus] = useState<AuthState>(null);

  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      instance.loginRedirect(loginRequest).catch(console.error);
    }
  }, [isAuthenticated, inProgress, instance]);

  useEffect(() => {
    if (isAuthenticated && approvalStatus === null) {
      setApprovalStatus('checking');
      usersApi.me()
        .then(() => setApprovalStatus('approved'))
        .catch((err: any) => {
          const status = err.response?.status;
          const code = err.response?.data?.error;
          if (status === 403 && code === 'pending_approval') {
            setApprovalStatus('pending');
          } else if (status === 403 && code === 'deactivated') {
            setApprovalStatus('deactivated');
          } else if (status === 401) {
            // Token was rejected by the backend — don't let the user into a
            // half-broken session where every API call silently fails.
            setApprovalStatus('invalid_token');
          } else {
            // Network blip or transient backend error — let the user through;
            // page-level handlers will surface specific errors.
            setApprovalStatus('approved');
          }
        });
    }
  }, [isAuthenticated, approvalStatus]);

  // Helper to fully sign out and clear the local MSAL cache so the next sign-in
  // re-acquires a fresh token (this is what fixes a stale/wrong-scope token).
  const handleSignOut = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  if (inProgress !== InteractionStatus.None || !isAuthenticated) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh" gap={2}>
        <CircularProgress size={48} />
        <Typography color="text.secondary">
          {inProgress === InteractionStatus.None ? 'Redirecting to sign in...' : 'Authenticating...'}
        </Typography>
      </Box>
    );
  }

  if (approvalStatus === 'checking' || approvalStatus === null) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh" gap={2}>
        <CircularProgress size={48} />
        <Typography color="text.secondary">Loading your account...</Typography>
      </Box>
    );
  }

  if (approvalStatus === 'pending') {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh" bgcolor="background.default">
        <Paper elevation={0} sx={{ p: 6, maxWidth: 480, textAlign: 'center', border: '1px solid #e0e0e0', borderRadius: 3 }}>
          <LockClockIcon sx={{ fontSize: 56, color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>Account Pending Approval</Typography>
          <Typography color="text.secondary" paragraph>
            Your account has been created but needs to be approved by an administrator before you can access COMP.
          </Typography>
          <Typography color="text.secondary" variant="body2" paragraph>
            Please contact <strong>jay@hyperlink.net</strong> to request access.
          </Typography>
          <Button variant="outlined" onClick={handleSignOut} sx={{ mt: 1 }}>
            Sign Out
          </Button>
        </Paper>
      </Box>
    );
  }

  if (approvalStatus === 'deactivated') {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh" bgcolor="background.default">
        <Paper elevation={0} sx={{ p: 6, maxWidth: 480, textAlign: 'center', border: '1px solid #e0e0e0', borderRadius: 3 }}>
          <BlockIcon sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>Account Deactivated</Typography>
          <Typography color="text.secondary" paragraph>
            Your COMP account has been deactivated.
          </Typography>
          <Typography color="text.secondary" variant="body2" paragraph>
            Please contact <strong>jay@hyperlink.net</strong> if you believe this is in error.
          </Typography>
          <Button variant="outlined" onClick={handleSignOut} sx={{ mt: 1 }}>
            Sign Out
          </Button>
        </Paper>
      </Box>
    );
  }

  if (approvalStatus === 'invalid_token') {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh" bgcolor="background.default">
        <Paper elevation={0} sx={{ p: 6, maxWidth: 520, textAlign: 'center', border: '1px solid #e0e0e0', borderRadius: 3 }}>
          <LockIcon sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>Sign-in Issue</Typography>
          <Typography color="text.secondary" paragraph>
            We couldn't validate your sign-in with COMP. Your Microsoft sign-in
            worked, but the access token COMP received is missing or invalid.
          </Typography>
          <Typography color="text.secondary" variant="body2" paragraph>
            Please sign out and sign back in. If the problem persists, contact{' '}
            <strong>jay@hyperlink.net</strong> — an administrator may need to grant
            consent for the COMP API on your behalf.
          </Typography>
          <Button variant="contained" onClick={handleSignOut} sx={{ mt: 1 }}>
            Sign Out and Try Again
          </Button>
        </Paper>
      </Box>
    );
  }

  return <>{children}</>;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthGate>
          <UserProvider>
          <Layout>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/contracts" element={<ContractList />} />
              <Route path="/contracts/new" element={<CreateContract />} />
              <Route path="/contracts/:id" element={<ContractDetail />} />
              <Route path="/contracts/:id/edit" element={<CreateContract />} />
              <Route path="/obligations" element={<ObligationList />} />
              <Route path="/obligations/:id" element={<ObligationDetail />} />
              <Route path="/notifications" element={<NotificationList />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/preferences" element={<PreferencesPage />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Layout>
          </UserProvider>
        </AuthGate>
      </Router>
    </ThemeProvider>
  );
}

export default App;
